import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DeliveryTrackerCloseAndNotifyStatuses,
  DeliveryTrackerNotifyOnlyStatuses,
  DeliveryTrackerResultContentTypes,
  DeliveryTrackerResultUploadLimits,
} from '@features/organization/components/delivery-tracker-detail/configuration/delivery-tracker-result-upload.config';
import type {
  DeliveryTrackerResultDelivery,
  DeliveryTrackerResultUploadDialogData,
} from '@features/organization/components/delivery-tracker-detail/types/delivery-tracker-document.types';
import { TranslocoDirective } from '@jsverse/transloco';
import type { ResponseDeliveryTrackerCacheDto } from '@kerberos-compliance/compliance-shared-ts/features/delivery/dtos/response.delivery-tracker.cache-dto';
import { TranslationsDeliveryTrackerStatus } from '@kerberos-compliance/compliance-shared-ts/features/delivery/i18n/delivery-tracker-status.translation';
import {
  buildKnownContentTypeOptions,
  ButtonComponent,
  ButtonVariantMap,
  FileInputComponent,
  ModalSizeMap,
  ModalWrapperComponent,
  TranslationService,
} from '@kerberos-compliance/compliance-shared-ui';
import { BusyOverlayComponent } from '@shared/components/busy-overlay/busy-overlay.component';
import { StagedFileListComponent } from '@shared/components/staged-file-list/staged-file-list.component';
import type { StagedFilePatchEvent } from '@shared/types/staged-file.type';
import { createStagedFileList } from '@shared/utils/staged-file-list.utility';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';

@Component({
  selector: 'aad-delivery-tracker-result-upload-dialog',
  imports: [
    BusyOverlayComponent,
    ButtonComponent,
    FileInputComponent,
    FormsModule,
    ModalWrapperComponent,
    NzCheckboxModule,
    StagedFileListComponent,
    TranslocoDirective,
  ],
  templateUrl: 'delivery-tracker-result-upload-dialog.component.html',
  styleUrl: 'delivery-tracker-result-upload-dialog.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryTrackerResultUploadDialogComponent {
  protected readonly ButtonVariantMap = ButtonVariantMap;

  protected readonly ModalSizeMap = ModalSizeMap;

  protected readonly uploadLimits = DeliveryTrackerResultUploadLimits;

  private readonly dialogData = inject<DeliveryTrackerResultUploadDialogData>(NZ_MODAL_DATA);

  protected readonly deliveryTracker: ResponseDeliveryTrackerCacheDto = this.dialogData.tracker;

  protected readonly isDelivering = signal(false);

  protected readonly canCloseAndNotify: boolean = DeliveryTrackerCloseAndNotifyStatuses.includes(
    this.deliveryTracker.status,
  );

  protected readonly isNotifyOnly: boolean = DeliveryTrackerNotifyOnlyStatuses.includes(this.deliveryTracker.status);

  protected readonly closeTrackerAndNotify = signal<boolean>(this.canCloseAndNotify && !this.isNotifyOnly);

  private readonly modalRef = inject(NzModalRef);

  private readonly translationService = inject(TranslationService);

  protected readonly stagedFiles = createStagedFileList(DeliveryTrackerResultUploadLimits.maxFiles);

  protected readonly trackerStatusLabel = computed(
    () => TranslationsDeliveryTrackerStatus[this.translationService.activeLanguage()][this.deliveryTracker.status],
  );

  protected readonly contentTypeOptions = computed(() =>
    buildKnownContentTypeOptions(this.translationService, DeliveryTrackerResultContentTypes),
  );

  protected readonly untypedFileCount = computed(
    () => this.stagedFiles.files().filter((staged) => staged.knownContentType === undefined).length,
  );

  protected readonly canSubmit = computed(
    () => this.stagedFiles.files().length > 0 && this.untypedFileCount() === 0 && !this.isDelivering(),
  );

  /**
   * Applies a staged file's metadata change reported by the staged-file list.
   *
   * @param {StagedFilePatchEvent} event - The addressed file and the fields it changed.
   * @returns {void} This method does not return a value.
   */
  protected onFilePatched(event: StagedFilePatchEvent): void {
    this.stagedFiles.patch(event.id, event.patch);
  }

  /**
   * Submits the assembled delivery and closes the dialog once it went through; on failure the dialog stays open
   * with the staged files so the user can retry. A status that forbids closing wins over the checkbox, so the
   * delivery can never carry a decision this tracker is not allowed to make.
   *
   * @returns {Promise<void>} Resolves once the delivery attempt has settled.
   */
  protected async submit(): Promise<void> {
    if (!this.canSubmit()) {
      return;
    }
    const delivery: DeliveryTrackerResultDelivery = {
      files: this.stagedFiles.files(),
      closeTrackerAndNotify: this.canCloseAndNotify && this.closeTrackerAndNotify(),
    };

    this.isDelivering.set(true);
    try {
      if (await this.dialogData.deliver(delivery)) {
        this.modalRef.close(true);
      }
    } finally {
      this.isDelivering.set(false);
    }
  }

  /**
   * Closes the dialog without delivering anything. Ignored while a delivery is in flight, so its outcome is
   * never lost behind a closed dialog.
   */
  protected cancel(): void {
    if (this.isDelivering()) {
      return;
    }
    this.modalRef.close();
  }
}
